import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API_BASE, STRIPE_PUBLISHABLE_KEY } from "../config";
import { startTracking, track } from "../track";
import { formatDate, words } from "../preview/copy";
import type { Language as Lang } from "../types";
import Hero from "../preview/Hero";
import BaseShowcase, { BaseStage } from "../preview/BaseShowcase";
import { baseKindOf, takesBase } from "../preview/christmas";
import Feedback from "../preview/Feedback";
import { DetailsCard, PriceCard } from "../preview/Facts";
import PriceLine from "../preview/PriceLine";
import OrderBar, { type SizeAsk } from "../preview/OrderBar";
import OrderProgress from "../preview/OrderProgress";
import { warmPayment } from "../preview/OrderSheet";
import LanguagePicker, { rememberLanguage, storedLanguage } from "../preview/LanguagePicker";
import { htmlLangOf, sid } from "../types";
import PieceOffer from "../preview/PieceOffer";
import PiecesIntro from "../preview/PiecesIntro";
import PieceRail from "../preview/PieceRail";
import { BasketBar, BasketSummary } from "../preview/Basket";
import { basketOf, BasketContext, pieceKey, qtySel, selectable, type BasketInfo } from "../preview/selection";
import { KIND_KEY } from "../preview/kinds";
import OrderAll, { togetherPieces, type TogetherAsk } from "../preview/OrderAll";
import Suggestions, { BackChip, type Suggestion } from "../preview/Suggestions";
import { layoutOf, rolesOf } from "../preview/primary";
import Proof, { pairsFor } from "../preview/Proof";
import Reviews from "../preview/Reviews";
import NewsletterOffer from "../preview/NewsletterOffer";
import { forgetPage, noteVisit, remembered, rememberSize, rememberStep } from "../preview/remember";
import Drawing from "../preview/Drawing";
import PhotoCheck from "../preview/PhotoCheck";
import ChristmasLook from "../preview/ChristmasLook";
import Others from "../preview/Others";
import { fetchOthers, forgetPreviews, knownOthers, rememberPreview, type OtherPreview } from "../preview/otherPreviews";
import { checkoutResult, PageContext, type BaseChoice } from "../preview/page";
import { rereadDelay } from "../preview/offerWindow";
import { CONTACT_EMAIL, Contact, Footer, Shell, Spliced, guessLanguage, siteUrl } from "../preview/chrome";
import type { Cutout, Language, PreviewDoc, PreviewFixing } from "../types";

/** The page the client gets a link to.
 *
 *  There is no account and no login: the token in the URL is the whole of their
 *  authority, and it can only read. It is opened on a phone, on cellular, by
 *  someone deciding whether to spend money, which is why the picture comes
 *  first and why the model, at tens of megabytes, waits to be asked for.
 *
 *  Everything below the picture is optional by design. The preview documents
 *  are snapshots, so a link emailed before a field existed simply has no value
 *  for it, and every section renders nothing rather than an empty frame. */
export default function Preview() {
  const { token = "" } = useParams();
  /** The studio's own look, from "See what they see". It reads the page exactly
   *  as the client would but is not them: no open counted, nothing tracked, and
   *  no language chosen on their behalf. */
  const [params, setParams] = useSearchParams();
  const admin = params.get("admin") === "1";
  /** Which mail the link was tapped in, when it says: every link to the page in
   *  a mail carries ?src=, and the server records it with the open. Passed on
   *  as it came, if it looks like one of those names at all; the server keeps
   *  its own list of the real ones. */
  const via = mailSource(params.get("src"));
  /** Paying on the page. For a customer, the server says whether they are
   *  offered it (preview.checkout, from CHECKOUT_AUDIENCE). For the studio's
   *  look (?admin=1) it is offered once this browser is known to be signed in
   *  as an admin, which is the same allow-list the server checks again; the
   *  sign-in and Firestore are only imported for that, never on a customer's
   *  page. */
  const [adminCheckout, setCheckout] = useState(false);
  /** Came from the order mail's "Pay on my page": the order sheet opens by
   *  itself, on the one-piece bar, or on a page of several on the card of the
   *  piece the mail was sent for (&piece=). A mail from before it named one
   *  opens nothing there: guessing would open the wrong piece's sheet for two
   *  people out of three.
   *
   *  The same ?piece= is the piece a page of several shows, when it shows one
   *  at a time (the row layout, preview/primary.ts): the mail's piece is then
   *  the page, and its bar is the sheet that opens. */
  const payNow = params.get("pay") === "1";
  const payPiece = params.get("piece") ?? "";
  useEffect(() => {
    if (!admin || !STRIPE_PUBLISHABLE_KEY) return;
    let live = true;
    void (async () => {
      const [{ auth }, { db }, { doc, getDoc }] = await Promise.all([
        import("../admin"),
        import("../firebase"),
        import("firebase/firestore"),
      ]);
      await auth.authStateReady();
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      const listed = await getDoc(doc(db, "admins", uid));
      if (live && listed.exists()) setCheckout(true);
    })().catch(() => undefined);
    return () => {
      live = false;
    };
  }, [admin]);
  /** Back from Stripe's return URL, ?paid=cs_...: a bank's app (iDEAL, MB WAY)
   *  sends them here whether they paid or not, a cancel included, so the URL
   *  only says which session it was. What became of it is asked of the server,
   *  which asks Stripe, and nothing is said until it has answered. This page
   *  used to thank anyone who came back with the parameter, so a payment
   *  cancelled at the bank was told its order was in, and had no reason to
   *  try again. The sale itself is still only what Stripe's webhook tells the
   *  server; this is what the page says about it. */
  const paidSession = params.get("paid") ?? "";
  const [returned, setReturned] = useState<Returned | null>(
    paidSession.startsWith("cs_") ? "checking" : null
  );
  /** How many times the return has been asked about. Only an answer that
   *  never came is asked again. */
  const [asks, setAsks] = useState(0);
  useEffect(() => {
    if (!token || !paidSession.startsWith("cs_")) return;
    let live = true;
    void checkoutResult(token, paidSession).then((result) => {
      if (!live) return;
      // Paid, and since given back in full: nothing to say about it now, and
      // the page is whatever the server says it is.
      if (result === "refunded") {
        setReturned(null);
        dropPaid(paidSession);
        return;
      }
      // Not being able to ask is its own answer. Said as a failure, it told
      // somebody who had paid that they had not, and offered them the checkout
      // again; said as a success, it would thank somebody whose bank refused.
      // It stays in the address, so a reload asks again, and the page offers
      // nothing to pay until it knows.
      setReturned(result);
      // A payment that did not happen is news for this visit only. Left in the
      // address, a reload, or this link from the history next week after they
      // ordered on Etsy, would say it again. One that did happen stays, and is
      // asked again on a reload, which is the answer the server has by then.
      if (result === "failed") dropPaid(paidSession);
    });
    return () => {
      live = false;
    };
  }, [token, paidSession, asks]);
  /* Not known yet: asked again when they come back to the tab, and once more a
     little later if they stay, the same as the page itself is re-read. The
     answer on screen stays until a new one comes. */
  useEffect(() => {
    if (returned !== "unknown") return;
    let timer: number | undefined;
    const again = () => setAsks((n) => n + 1);
    const onVisible = () => {
      window.clearTimeout(timer);
      if (document.visibilityState === "visible") timer = window.setTimeout(again, RETURN_DELAY_MS);
    };
    const later = window.setTimeout(again, UNCONFIRMED_AGAIN_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(later);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [returned]);
  /** Paid in the sheet on this visit: "checking" from the moment Stripe says
   *  the checkout is done, then what the server made of it. The sheet says
   *  thank you itself; this is so that the page goes on saying it after the
   *  sheet is closed, and stops offering to take the money again, before any
   *  re-read could tell it the same. The session goes into the address, as a
   *  bank's return puts it there, so a reload asks about it rather than
   *  offering the checkout again until Stripe's webhook has landed. */
  const [paidHere, setPaidHere] = useState<"checking" | "paid" | "processing" | null>(null);
  /** The arrival window of an express payment made in the sheet, said until
   *  the page is read again (the server then says it from the day it was
   *  paid). */
  const [paidArrives, setPaidArrives] = useState("");
  const markPaid = useCallback((result: "checking" | "paid" | "processing", session: string, arrives?: string) => {
    setPaidHere(result);
    if (arrives) setPaidArrives(arrives);
    if (result === "checking") keepPaid(session);
  }, []);
  const [preview, setPreview] = useState<PreviewDoc | null | Trouble>(null);
  /** An instant preview that has just arrived while they watched: the bar
   *  fills and says "Done!" for a moment before the figurine takes its place,
   *  which then comes in with a small animation. */
  const [finishing, setFinishing] = useState(false);
  const [arrived, setArrived] = useState(false);
  const watching = useRef(false);
  /** A change being fixed while they watch (functions/src/lib/livefix.ts).
   *  `fixingHere` is the moment between their tap and the server's first word
   *  about it, so the drawing goes grey on the tap rather than a read later;
   *  any answer to a read asked after the tap (`fixFrom`) replaces it.
   *  `fixDone` is the "Done!" moment once the fix has landed, held for
   *  FINISH_MS before the new drawing takes its place, as the first one did. */
  const [fixingHere, setFixingHere] = useState<PreviewFixing | null>(null);
  const [fixDone, setFixDone] = useState<PreviewFixing | null>(null);
  const fixFrom = useRef(Number.POSITIVE_INFINITY);
  /** The fix last read, and the drawings of its pieces as they were when it
   *  started, which is how its landing is told from its falling back: the
   *  drawing changed. */
  const lastFixing = useRef<{ fixing: PreviewFixing; urls: string[] } | null>(null);
  /** Whether a preview has come back on this visit. Once one is on screen,
   *  nothing that goes wrong afterwards may take it away again. */
  const loaded = useRef(false);
  /** Which read was asked for last, and which one is on screen: an older answer
   *  that arrives late must not replace a newer one. */
  const asked = useRef(0);
  const shown = useRef(0);
  /** Whichever language they last chose, on any preview. Null until they do,
   *  and then the page is read in the one we guessed from the form. Held in a
   *  ref as well so that re-reading the page does not depend on it and re-run
   *  on every change. The studio's look starts from the client's language, not
   *  whichever one this browser last picked on some other preview. */
  const chosen = useRef<Language | null>(admin ? null : storedLanguage());
  /** Whether the server has already counted this visit as an open. It counts
   *  only a sent preview, so until one has come back ready there is nothing a
   *  second read would repeat. */
  const counted = useRef(false);
  /** Counts the taps on "Choose your pieces" after "Love it" on a page of
   *  several, which "Order them together" answers by opening its sheet. */
  const [togetherAsk, setTogetherAsk] = useState(0);
  /** On a page of several that lets them choose: which pieces are in their
   *  order and at which size (selection.ts). For this visit only. */
  const [picks, setPicks] = useState<Record<string, string | null>>({});
  const onPick = useCallback((key: string, size: string | null) => {
    setPicks((before) => (before[key] === size ? before : { ...before, [key]: size }));
  }, []);
  /** A size tapped in the price card's "By size" ladder: the bar's sheet opens
   *  on it, on the payment form. */
  const [sizeAsk, setSizeAsk] = useState<SizeAsk | null>(null);
  /** The size they have picked: on this visit, or on an earlier one on this
   *  device (preview/remember.ts). Every order button names it, the ladder
   *  draws it pressed, and the sheet opens on it. The studio's look starts
   *  from nothing, as a first visit does. */
  const [size, setSize] = useState<string | null>(() => (admin ? null : (remembered(token)?.size ?? null)));
  /** Which piece of a page that shows one at a time the size and the step
   *  below belong to: none (null) for the page's own piece, which is a page of
   *  one's only piece and the piece a page of several is about, and otherwise
   *  the id of another piece shown in its place. The device remembers the
   *  first alone, under the page's token, so a size picked for a Pair is never
   *  the size the Pair's single opens on; another piece keeps its own for this
   *  visit and no longer. Set as the page is drawn, read by the callbacks
   *  that are made before it is. */
  const slot = useRef<string | null>(null);
  const [pieceSizes, setPieceSizes] = useState<Record<string, string>>({});
  const onSize = useCallback(
    (next: string) => {
      const other = slot.current;
      if (other !== null) {
        setPieceSizes((before) => (before[other] === next ? before : { ...before, [other]: next }));
        return;
      }
      setSize(next);
      if (!admin) rememberSize(token, next);
    },
    [token, admin]
  );
  /** How far they got in the order sheet on this device: "pay" once they
   *  asked for the form, which the button then offers to go back to. */
  const [step, setStep] = useState<"pick" | "pay" | null>(() => (admin ? null : (remembered(token)?.step ?? null)));
  const [pieceSteps, setPieceSteps] = useState<Record<string, "pick" | "pay">>({});
  const onStep = useCallback(
    (next: "pick" | "pay") => {
      const other = slot.current;
      if (other !== null) {
        setPieceSteps((before) => (before[other] === "pay" || before[other] === next ? before : { ...before, [other]: next }));
        return;
      }
      setStep((before) => (before === "pay" ? before : next));
      if (!admin) rememberStep(token, next);
    },
    [token, admin]
  );
  /** The pieces they said "Love it" to on this visit, on a page that shows
   *  one at a time: the card is made again when they come back to a piece, and
   *  would ask again what they have just answered, until the page is read
   *  again. */
  const [lovedHere, setLovedHere] = useState<string[]>([]);
  /** A tap on a suggestion's "Add ... too": the together sheet opens with the
   *  piece the page shows and that one. */
  const [togetherAdd, setTogetherAdd] = useState<TogetherAsk | null>(null);
  /** Whether the server has been told they want to order from a suggestion
   *  this visit: once, as the basket's continue is. */
  const suggestionWanted = useRef(false);
  /** How many of it they chose in an order sheet, per piece (qtyKey()), for
   *  the rest of this visit and no longer: the sheet that held it is gone
   *  once closed, and the button that reopens it has to say, and open on,
   *  the same number. Only ever set where the page decides how many (the
   *  multi-buy). Not on the device, unlike the size: a later visit starts
   *  at one. */
  const [qtys, setQtys] = useState<Record<string, number>>({});
  const onQty = useCallback((key: string, next: number) => {
    setQtys((before) => (before[key] === next ? before : { ...before, [key]: next }));
  }, []);
  /** The Christmas base switched on in a sheet or for a piece, with the
   *  message typed for it, under the same keys as `qtys` and for the same
   *  reason: the sheet that held it is gone once closed, and the one that
   *  opens again, or "Order them together", must open on it and the form
   *  charge for it (ChristmasBase.tsx). For this visit only. */
  const [bases, setBases] = useState<Record<string, BaseChoice>>({});
  const onBase = useCallback((key: string, next: BaseChoice) => {
    setBases((before) => {
      const had = before[key];
      return had && had.on === next.on && had.message === next.message ? before : { ...before, [key]: next };
    });
  }, []);
  /* The Christmas base asked for on the request form (Lead.baseWish, sent as
     christmas.base.wish): the page opens with it switched on and the message
     in, under every key a sheet, a card or the showcase reads it by. Only where
     nothing has been chosen yet this visit, so a tap on "Plain base" is never
     undone. */
  const wishMessage = typeof preview === "object" && preview ? preview.christmas?.base?.wish?.message : undefined;
  useEffect(() => {
    if (wishMessage === undefined || typeof preview !== "object" || !preview) return;
    const list = preview.pieces?.length ? preview.pieces : [{ id: "", kind: undefined }];
    const keys = list
      .filter((piece) => takesBase(baseKindOf(piece.kind ?? preview.kind, "price" in piece ? piece.price : null)))
      .flatMap((piece) => [
        ...(list.length > 1 ? [] : ["page"]),
        `piece:${piece.id}`,
        qtySel(pieceKey("", piece.id)),
      ]);
    if (!keys.length) return;
    setBases((before) => {
      const next = { ...before };
      let changed = false;
      for (const key of keys) {
        if (!next[key]) {
          next[key] = { on: true, message: wishMessage };
          changed = true;
        }
      }
      return changed ? next : before;
    });
    // preview itself changes on every poll; the wish is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wishMessage]);
  /** How many times this device has opened the page, this time included,
   *  once the page is ready; 0 until then, and always in the studio's look. */
  const [visits, setVisits] = useState(0);
  /* Before the preview has loaded there is no language to read the page in, and
     the two screens most likely to appear there — no connection, and a link
     that opens nothing — are the two most likely to worry somebody. They used
     to be hardcoded English, so a Portuguese lead whose train went through a
     tunnel got a sentence in a language they may not read, at the one moment
     the page had nothing else to offer. This is a guess from the browser, which
     is the same quality of guess the form made. */
  const guessed: Lang = chosen.current ?? guessLanguage();

  /** One read of the page.
   *
   *  "first" is the read the page opens with, and a retry of it: the only one
   *  allowed to put an error on the screen, and only while nothing has loaded.
   *  "language" and "return" are re-reads of a page somebody is already
   *  looking at, and a failure of either leaves the page as it was. */
  const load = useCallback(
    (why: "first" | "language" | "return" | "poll" = "first") => {
      if (!token) return;
      const query = new URLSearchParams({ token });
      if (chosen.current) query.set("lang", chosen.current);
      // Same person, same visit, second look: a language switch, or coming back
      // to the tab. Only the first counts as an open. Coming back used to count
      // every time, so a phone that switched to Etsy and back read as two opens.
      // A pending page that turns ready while they were away is their first
      // look at it, so coming back only says "reread" once a ready one was read.
      if (why === "language" || counted.current) query.set("reread", "1");
      if (admin) query.set("admin", "1");
      if (via) query.set("src", via);
      const url = `${API_BASE}/api/preview?${query}`;
      const mine = ++asked.current;
      // index.html starts the first read before this bundle has even arrived.
      // Not for the studio's look, which reads with the owner's sign-in.
      void (admin ? adminRead(url) : (takeEarlyRead(url) ?? fetch(url)))
        .then((r) => (r.ok ? r.json() : Promise.reject(new ReadFailed(r.status))))
        .then((data: PreviewDoc) => {
          if (mine < shown.current) return;
          shown.current = mine;
          loaded.current = true;
          if (data.state === "ready") counted.current = true;
          if (data.state === "pending" && data.instant && data.drawing) watching.current = true;
          if (data.state === "ready" && watching.current) {
            watching.current = false;
            setFinishing(true);
            window.setTimeout(() => {
              setFinishing(false);
              setArrived(true);
              setPreview(data);
            }, FINISH_MS);
            return;
          }
          if (mine >= fixFrom.current) setFixingHere(null);
          if (data.state === "ready" && data.fixing) {
            lastFixing.current = {
              fixing: data.fixing,
              urls: lastFixing.current?.urls ?? mockupsOf(data, data.fixing.subjectIds),
            };
          } else if (lastFixing.current) {
            const was = lastFixing.current;
            lastFixing.current = null;
            // Landed: a drawing it was fixing is not the one it started from,
            // so "Done!", then the new drawing. Otherwise it fell back to the
            // studio, and the page says it will be redone, which the card
            // under it reads from the open change.
            const now = data.state === "ready" ? mockupsOf(data, was.fixing.subjectIds) : was.urls;
            if (now.some((url, at) => url !== was.urls[at])) {
              setFixDone(was.fixing);
              window.setTimeout(() => {
                setFixDone(null);
                setArrived(true);
                setPreview(data);
              }, FINISH_MS);
              return;
            }
          }
          setPreview(data);
        })
        .catch((err: unknown) => {
          // A preview already on screen stays there, whatever went wrong with a
          // re-read of it. Coming back to the tab used to replace it with "This
          // link does not open anything" on any failure at all, and on iOS the
          // first request after returning from another app is the one most
          // likely to fail, 20 to 40 seconds later, while they are reading.
          // A failed language switch keeps the page they already have for the
          // same reason: losing a preview because a dropdown moved would be
          // worse than staying in the old language.
          if (why !== "first" || loaded.current) return;
          // Only a 404 means the link opens nothing. A server error, a body cut
          // short or a request that never got an answer is worth another try,
          // and offline the shell comes from the worker's cache and this is the
          // only request that fails: telling someone their link is dead would
          // be a lie either way.
          if (err instanceof ReadFailed && err.status === 404) setPreview("missing");
          else setPreview(navigator.onLine ? "error" : "offline");
        });
    },
    [token, admin, via]
  );

  useEffect(() => load(), [load]);

  /** For the checkout, when the server refused because the page is out of
   *  date: the prices on it, the piece, or that it is already paid for. */
  const refresh = useCallback(() => load("return"), [load]);

  /** Their other previews, for the strip under the heading (Others.tsx): null
   *  until asked, and only ever asked by a browser that has opened another
   *  preview, so somebody with one preview, which is most people, sends
   *  nothing. Never in the studio's look, which is not them. */
  const [others, setOthers] = useState<OtherPreview[] | null>(null);
  /** Being asked for the first time, so the strip's place is held open. */
  const [othersWait, setOthersWait] = useState(false);
  const othersAsked = useRef(0);
  const othersShown = useRef(false);
  othersShown.current = !!others && others.length > 1;
  /** The language the page is read in, for the prices of their other
   *  previews in "Order them together", which are quoted in it. */
  const readIn = useRef<Language | null>(null);
  readIn.current = typeof preview === "object" && preview !== null ? preview.language : null;
  const loadOthers = useCallback(() => {
    if (admin || !token) return;
    const known = knownOthers(token);
    if (!known.length) return;
    const mine = ++othersAsked.current;
    void fetchOthers(token, known, readIn.current).then((list) => {
      if (mine !== othersAsked.current) return;
      setOthersWait(false);
      // A failed ask keeps whatever the strip already said.
      if (list) setOthers(list);
    });
  }, [token, admin]);

  function chooseLanguage(next: Language) {
    chosen.current = next;
    // The studio trying a language is not the client choosing one, and must
    // not follow this browser onto the next preview it opens.
    if (!admin) rememberLanguage(next);
    load("language");
  }

  /** Back to the skeleton and ask again, rather than reloading: offline, a
   *  reload is the one thing guaranteed to show the same screen again. */
  function retry() {
    setPreview(null);
    load("first");
  }

  // The page used to claim it was live and was not: a change redone while the
  // tab sat open showed nothing, forever. Coming back to the tab re-reads it.
  // Not at once: on iOS a request started the moment Safari comes back from
  // another app can fail with "Load failed" tens of seconds later, where one
  // started a moment afterwards goes through. Somebody back from Etsy is
  // looking at the preview they already have, so there is no hurry.
  useEffect(() => {
    let timer: number | undefined;
    const onVisible = () => {
      window.clearTimeout(timer);
      if (document.visibilityState === "visible") {
        timer = window.setTimeout(() => {
          load("return");
          // A preview of theirs may have been sent or paid for meanwhile,
          // perhaps in the other tab they went to.
          if (othersShown.current) loadOthers();
        }, RETURN_DELAY_MS);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load, loadOthers]);

  /* An instant preview being drawn is checked again every few seconds, so it
     appears on its own the moment it is sent. Only while a drawing is actually
     under way: one that fell back to the studio will come by email, and the
     page says so rather than being asked about it for a day. A failed check
     leaves the page as it is, like any other re-read. */
  const drawingNow =
    typeof preview === "object" &&
    preview !== null &&
    preview.state === "pending" &&
    !!preview.instant &&
    !!preview.drawing &&
    !finishing;
  /* A change being fixed live is checked as often as a drawing that streams:
     it is the drawing they are looking at, and the rough frames of the fix
     come through the same way. */
  const fixingNow =
    typeof preview === "object" &&
    preview !== null &&
    preview.state === "ready" &&
    (!!preview.fixing || !!fixingHere) &&
    !fixDone;
  /* The photos did not show what was asked for, and the drawing waits for the
     customer: new photos being read, or Continue pressed, or nothing yet. The
     page is read again every few seconds so that it moves on by itself when the
     check lifts: to the drawing, if it is an instant preview, or to the plain
     waiting page. Slower than a drawing's poll, since nothing here streams and
     the reading takes a minute or so. */
  const checkingPhotos =
    typeof preview === "object" &&
    preview !== null &&
    preview.state === "pending" &&
    !!preview.photoCheck;
  useEffect(() => {
    if (!checkingPhotos) return;
    const timer = window.setTimeout(() => load("poll"), PHOTO_CHECK_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [checkingPhotos, preview, load]);

  useEffect(() => {
    if (!fixingNow) return;
    const timer = window.setTimeout(() => load("poll"), SKETCH_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [fixingNow, preview, fixingHere, load]);

  useEffect(() => {
    if (!drawingNow) return;
    // Sooner while the drawing is streaming, so each rough frame shows up
    // close to when it was drawn rather than up to five seconds after.
    const wait = preview.drawingPhase === "drawing" ? SKETCH_POLL_MS : POLL_MS;
    const timer = window.setTimeout(() => load("poll"), wait);
    return () => window.clearTimeout(timer);
  }, [drawingNow, preview, load]);

  /* A ready page left open is read again when what the server says would
     change: at the end of a dated window of the multi-buy (the `until` of any
     price on it, their other previews' in "Order them together" included),
     and at midnight in Lisbon, when a window starts, its framing turns from
     Christmas to Black Friday, and the delivery dates move
     (preview/offerWindow.ts). Without it a page open across 3 December kept
     offering −35% until the checkout refused it as out of date. A few seconds
     apart from one page to the next, so open pages do not all ask at once. A
     failed read leaves the page as it is, like any other re-read. */
  const readyPage = typeof preview === "object" && preview !== null && preview.state === "ready" ? preview : null;
  useEffect(() => {
    if (!readyPage) return;
    const untils = [
      readyPage.price?.multiBuy?.until,
      ...(readyPage.pieces ?? []).map((p) => p.price?.multiBuy?.until),
      ...(others ?? []).flatMap((o) => (o.basket ?? []).map((b) => b.price.multiBuy?.until)),
    ];
    const wait = rereadDelay(untils, Date.now()) + Math.floor(Math.random() * REREAD_SPREAD_MS);
    const timer = window.setTimeout(() => {
      load("return");
      if (othersShown.current) loadOthers();
    }, wait);
    return () => window.clearTimeout(timer);
  }, [readyPage, others, load, loadOthers]);

  useEffect(() => {
    if (typeof preview !== "object" || preview === null) return;
    if (preview.state !== "ready") return;
    // Nothing the studio does on the page is the client doing it.
    if (admin) return;
    return startTracking(token);
  }, [preview, token, admin]);

  /* This page, into the list of the ones this browser has opened, once it is
     known to be a page at all: while it is still being drawn too, so that the
     second preview of somebody who asked for two in a row is in the first one's
     strip while they wait. A page that is gone, or opens nothing, comes out. */
  useEffect(() => {
    if (admin || preview === null) return;
    if (preview === "missing") {
      forgetPreviews([token]);
      forgetPage(token);
    }
    if (typeof preview !== "object") return;
    if (preview.state === "purged") {
      forgetPreviews([token]);
      forgetPage(token);
    } else rememberPreview(token);
  }, [preview, token, admin]);

  /* The strip, asked for once the preview is there to be looked at, which is
     the only screen it is on. */
  const readyNow = typeof preview === "object" && preview !== null && preview.state === "ready";
  /* This open of the page, counted on the device, once per load: the page
     that opens for the sixth time is the one most likely to be the buying
     visit, and the size it remembers is the one the button names. */
  const visited = useRef(false);
  useEffect(() => {
    if (!readyNow || admin || visited.current) return;
    visited.current = true;
    setVisits(noteVisit(token).visits);
  }, [readyNow, admin, token]);
  useEffect(() => {
    if (!readyNow || admin || !knownOthers(token).length) return;
    setOthersWait(true);
    loadOthers();
  }, [readyNow, admin, token, loadOthers]);

  /* Read in another language: their other previews' prices in "Order them
     together" are quoted in it too. Asked again whenever they have been
     asked for at all, the first answer included: one still on its way was
     priced in the language before, and is dropped for this one. */
  const language = typeof preview === "object" && preview !== null ? preview.language : null;
  const askedIn = useRef<Language | null>(null);
  useEffect(() => {
    if (!language) return;
    const before = askedIn.current;
    askedIn.current = language;
    if (before && before !== language && othersAsked.current > 0) loadOthers();
  }, [language, loadOthers]);

  /* Paid on this visit, in the sheet or by a bank that sent them back here:
     their tile says so, and the ones still to order are the ones left
     without a tick. */
  const paidNow = paidHere === "paid" || paidHere === "processing" || returned === "paid" || returned === "processing";
  useEffect(() => {
    if (paidNow && othersShown.current) loadOthers();
  }, [paidNow, loadOthers]);
  /* Paid for, here or earlier: nothing to continue to, so the device
     forgets the size and the step. The button is gone anyway; this is so
     the memory does not outlive the order. */
  const orderedNow =
    paidNow || (typeof preview === "object" && preview !== null && preview.state === "ready" && (!!preview.ordered || !!preview.processing));
  useEffect(() => {
    if (!orderedNow || admin) return;
    forgetPage(token);
    setStep(null);
    setPieceSteps({});
    setQtys({});
  }, [orderedNow, admin, token]);

  /* Paying here, made quick to open: once the page is ready for someone it
     offers paying to (the same test canPay makes below, and not for a page
     already paid for), Stripe.js and the form's own file are fetched, so
     "I want to order" opens on the form rather than on an empty box while they
     arrive. A moment after the drawing has had the connection to itself. */
  const payReady =
    typeof preview === "object" &&
    preview !== null &&
    preview.state === "ready" &&
    !!STRIPE_PUBLISHABLE_KEY &&
    (admin ? adminCheckout : !!preview.checkout && !preview.ordered && !preview.processing);
  useEffect(() => {
    if (!payReady) return;
    const timer = window.setTimeout(warmPayment, WARM_PAY_MS);
    return () => window.clearTimeout(timer);
  }, [payReady]);

  /* index.html ships lang="en" because it is one file serving eight languages and
     a client console. Left alone, Chrome and Safari offer to translate a page a
     Portuguese reader is already reading in Portuguese — which, on the page
     where they decide whether to trust us, reads as somebody else's website. */
  useEffect(() => {
    const language =
      typeof preview === "object" && preview !== null ? preview.language : guessed;
    // Chinese with its script (zh-Hant, zh-Hans), for the glyphs and the offer.
    document.documentElement.lang = htmlLangOf(language);
  }, [preview, guessed]);

  if (preview === null) {
    return (
      <Shell>
        <div className="card stack">
          <div className="skeleton" style={{ height: 300, borderRadius: 12 }} />
          <div className="skeleton" style={{ height: 46, borderRadius: 999 }} />
        </div>
      </Shell>
    );
  }

  if (typeof preview === "string") {
    const g = words(guessed);
    return (
      <Shell>
        <div className="card stack">
          <p className="muted" style={{ margin: 0 }}>{g.t(TROUBLE_COPY[preview])}</p>
          <button className="btn btn-soft btn-block" onClick={retry}>
            {g.t("retry")}
          </button>
          {/* A dead end with nobody behind it reads as a scam. A person who can
              be written to does not, and one mail is usually all it takes to
              send the right link again. */}
          <Contact words={g} ask="contactTrouble" />
        </div>
        <Footer words={g} language={guessed} />
      </Shell>
    );
  }

  const t = words(preview.language);

  if (preview.state === "purged") {
    return (
      <Shell picker={
        <LanguagePicker language={preview.language} words={t} onChange={chooseLanguage} />
      }>
        {/* Not the end of it. Gift buyers look in the autumn and come back to
            buy in December, from the same mail, to this page; it used to say
            only that it was gone, and asking again meant finding the site's
            form on their own. */}
        <div className="card stack">
          <h1>{t.t("title")}</h1>
          <p className="muted">{t.t("expired")}</p>
          <p style={{ margin: "14px 0 0" }}>{t.t("expiredAgain")}</p>
          <a
            className="btn btn-primary btn-block"
            href={siteUrl(preview.language, "", { utm_source: "preview", utm_medium: "expired" }, "preview")}
          >
            {t.t("expiredNew")}
          </a>
          <Contact words={t} ask="contactAsk" />
        </div>
        <Footer words={t} language={preview.language} />
      </Shell>
    );
  }

  if (preview.state === "pending" && preview.instant) {
    const live = !!preview.drawing || finishing;
    return (
      <Shell picker={
        <LanguagePicker language={preview.language} words={t} onChange={chooseLanguage} />
      }>
        {/* The instant-preview test: they came here straight from the form and
            the drawing is being made while they look. Their own photo stands
            in for it and comes to life as it goes, so the wait is visibly
            about them. */}
        {/* The photos did not show everything they asked for, so the drawing is
            on hold and the page asks them, in place of the waiting card: that
            one would say the preview is coming, which it is not until they
            answer. */}
        {preview.photoCheck ? (
          <PhotoCheck
            check={preview.photoCheck}
            token={token}
            language={preview.language}
            words={t}
            onChanged={refresh}
          />
        ) : (
          <div className="card stack">
            <h1>{live ? t.t("instantTitle") : t.t("pendingTitle")}</h1>
            {live ? (
              <Drawing
                words={t}
                photoUrl={preview.photoUrl ?? null}
                since={preview.drawingSince ?? null}
                phase={preview.drawingPhase ?? null}
                sketch={preview.drawingSketch ?? null}
                done={finishing}
              />
            ) : null}
            <p className="muted" style={{ margin: 0 }}>
              {live ? t.t("instantWhat") : t.t("instantSlow")}
            </p>
          </div>
        )}

        <Proof kind={preview.kind} words={t} />

        <Footer words={t} language={preview.language} />
      </Shell>
    );
  }

  if (preview.state === "pending") {
    return (
      <Shell picker={
        <LanguagePicker language={preview.language} words={t} onChange={chooseLanguage} />
      }>
        {/* Where the acknowledgement mail sends them, in the hours when they
            are most curious. It used to be one sentence. Now it says what is
            coming and when, makes sure the mail it arrives in is not lost to a
            spam folder, and shows what other people's previews turned into. */}
        {/* The photos did not show everything they asked for, so the drawing is
            on hold and the page asks them, in place of the waiting card: that
            one would say the preview is coming, which it is not until they
            answer. */}
        {preview.photoCheck ? (
          <PhotoCheck
            check={preview.photoCheck}
            token={token}
            language={preview.language}
            words={t}
            onChanged={refresh}
          />
        ) : (
          <div className="card stack">
            <h1>{t.t("pendingTitle")}</h1>
            <p className="muted" style={{ margin: 0 }}>{t.t("pendingWhat")}</p>
            <p className="small" style={{ margin: "10px 0 0" }}>
              <Spliced text={t.t("pendingInbox")} slot="{email}">
                <strong>{CONTACT_EMAIL}</strong>
              </Spliced>
            </p>
          </div>
        )}

        <Proof kind={preview.kind} words={t} />

        <Footer words={t} language={preview.language} />
      </Shell>
    );
  }

  /* One card per piece. A request for three dogs is three keychains, each drawn
     from its own photo, and showing only the first would quietly drop two thirds
     of what they asked for. A preview written before pieces existed has no list,
     which reads as the single piece it has always been: an id of "" matches the
     null or missing subjectId every artifact on it carries. */
  const pieceList = preview.pieces?.length
    ? preview.pieces
    : [{ id: "", photoUrl: preview.photoUrl ?? null }];
  const drawings = pieceList.map((piece) => {
    const mine = preview.artifacts.filter((a) => sid(a.subjectId) === sid(piece.id));
    return {
      piece,
      mockup: mine.find((a) => a.kind === "mockup"),
      model: mine.find((a) => a.kind === "model"),
      video: mine.find((a) => a.kind === "video"),
    };
  });
  const name = (preview.firstName ?? "").trim();
  const several = drawings.length > 1;
  /* The version of the page this reader is showing (preview/primary.ts), for a
     page of several: as the piece they asked for with the rest in one row of
     suggestions ("rowed"), or as the stack of equal cards it was before
     ("cards", the control of the test). A page of one is neither, and keeps
     the one-piece page it has always been. */
  const layout = layoutOf(preview, params, admin);
  const rowed = several && layout.pieces === "row";
  const cards = several && !rowed;
  const roles = rolesOf(pieceList, preview.kind);
  const primaryAt = Math.max(0, roles.indexOf("primary"));
  /* The piece a rowed page shows: the one the address names when it is one of
     this page's, else the one the page is about. Nothing on the page says
     which piece a page already paid for was paid for (the server tells only
     that it was), so it is the address's, which the sheet that took the
     payment keeps in it, and otherwise the one the page is about. */
  const namedAt = payPiece ? pieceList.findIndex((p) => p.id === payPiece) : -1;
  const showingAt = rowed ? (namedAt >= 0 ? namedAt : primaryAt) : -1;
  const showing = rowed ? drawings[showingAt]! : null;
  const showingKind = showing ? (showing.piece.kind ?? preview.kind) : preview.kind;
  /* What the size, the step, the count and the base on this page are about: for
     another piece than the page's own, its own for this visit
     (see `slot` above). */
  slot.current = showing && showingAt !== primaryAt ? showing.piece.id : null;
  /* The page as the one-piece page reads it, for the showing piece: its product,
     its price, its way to the shop, and nothing of the other pieces. The price
     card, the details, the proof and the order bar then read it exactly as
     they read a page of one, and cannot show or order another piece's. */
  const view: PreviewDoc = showing
    ? {
        ...preview,
        kind: showingKind,
        price: showing.piece.price ?? null,
        etsyUrl: showing.piece.etsyUrl ?? "",
        pieces: [showing.piece],
      }
    : preview;
  const fixing = fixDone ?? fixingHere ?? preview.fixing ?? null;
  /* An open change first: someone who loved it and then asked for a change is
     waiting on the change. After it is redone the question is open again,
     unless what they last said was that they love it. */
  const said: "loved" | "issue" | null =
    (preview.openChanges ?? 0) > 0 ? "issue" : preview.feedback === "loved" ? "loved" : null;
  /* The same for one piece of several: its own open change and its own love,
     where the server says which, and the page's answer for all of them where
     it does not (an older server). */
  const saidOf = (pieceId: string): "loved" | "issue" | null => {
    const open = preview.openPieces ? preview.openPieces.includes(pieceId) : (preview.openChanges ?? 0) > 0;
    if (open) return "issue";
    const loved = preview.lovedPieces ? preview.lovedPieces.includes(pieceId) : preview.feedback === "loved";
    return loved ? "loved" : null;
  };
  /* A change sent from a card: if it is being fixed while they watch, the
     pieces it is about go grey at once rather than a read later. Only the
     piece it was asked on, for a card of its own piece. */
  const startFixing = (live: boolean, subjectIds: string[]) => {
    if (!live) return;
    setFixingHere({
      subjectIds,
      since: new Date().toISOString(),
      phase: "reading",
      sketches: {},
    });
    fixFrom.current = asked.current + 1;
    load("poll");
  };

  /* Paying here, for whoever it is offered to: the server's word for a
     customer, the admin check for the studio's look, and for both only if this
     build has Stripe's key. Without one the form cannot be drawn, and .env
     promises that an empty key means Etsy only; the customer's side used to
     trust the server alone and got an empty payment box. */
  const canPay = !!STRIPE_PUBLISHABLE_KEY && (admin ? adminCheckout : !!preview.checkout);
  // The pieces as the order sheet lists them, and what is chosen of them so
  // far, where the page lets them choose (selection.ts).
  const togetherList = togetherPieces(drawings);
  /* The Christmas base's limits, where the server offers the base on this
     page (christmas.base: in its window, with "Pay here"); and only where the
     page can take the payment, since nothing else can carry it. */
  const baseLimits = canPay ? (preview.christmas?.base?.maxChars ?? null) : null;
  // Each statue drawing's figure alone, by the drawing's url, for every base
  // on the page to stand it on (BaseScene), wherever that drawing is showing.
  const cutouts: Record<string, Cutout> = {};
  for (const a of preview.artifacts) if (a.kind === "mockup" && a.cutout) cutouts[a.url] = a.cutout;
  /* Choosing pieces on cards is the cards' way; a page that shows one piece
     at a time orders that piece, or the pair of them from a suggestion. */
  const canPick = !rowed && selectable(togetherList, canPay);
  const basketInfo: BasketInfo = {
    on: canPick,
    basket: basketOf(togetherList, picks, qtys, preview.kind, canPay, bases, baseLimits),
    open: () => setTogetherAsk((n) => n + 1),
  };
  /* What this visit knows about paying, over what the server said when the
     page was read: a payment made in the sheet, or the one they have just come
     back from. While either is still being asked about, or could not be, the
     page does not offer to take the money again; nor while the server says a
     payment is on its way from the bank. */
  const visit = paidHere ?? (returned === "paid" || returned === "processing" ? returned : null);
  const ordered =
    !!preview.ordered ||
    !!preview.processing ||
    visit !== null ||
    returned === "checking" ||
    returned === "unknown";
  /* One line at the top about it, the newest news first. A payment still being
     confirmed that the server has since seen go through is simply paid. */
  const paidLine = lineFor({
    visit,
    returned,
    ordered: !!preview.ordered,
    processing: !!preview.processing,
  });
  /* The order mail's "Pay on my page", only when there is a payment form to
     open, and never onto a page that has already been paid for. */
  const payOpen = payNow && canPay && (admin || !ordered);

  /* A page that shows one piece at a time: swapping the piece it shows. The
     piece goes in the address (the page's own piece leaves it), so a reload
     and the back button show the same piece. Whatever else the address says
     stays (the studio's look, the mail it came from), except the order mail's
     ?pay=1, which asked for a sheet on the piece it named and must not open
     one on the next. Back at the top, where the drawing is. */
  const showPiece = (id: string | null) => {
    setParams((before) => {
      const next = new URLSearchParams(before);
      if (id) next.set("piece", id);
      else next.delete("piece");
      next.delete("pay");
      return next;
    });
    setSizeAsk(null);
    window.scrollTo({ top: 0 });
  };
  /* The other pieces, for the row under the price: the one the page is about
     first when another is showing, then the rest as they were drawn. */
  const suggestions: Suggestion[] = showing
    ? drawings
        .map((d, at) => ({ at, d }))
        .filter(({ at }) => at !== showingAt)
        .sort((a, b) => Number(b.at === primaryAt) - Number(a.at === primaryAt))
        .map(({ at, d }) => ({
          piece: d.piece,
          kind: d.piece.kind ?? preview.kind,
          role: roles[at] ?? "extra",
          drawingUrl: d.mockup?.url ?? d.piece.photoUrl ?? null,
        }))
    : [];
  /* The sheet of "Order them together" can take the showing piece with another
     when the page pays here and both are priced with their sizes. */
  const together = canPay && !ordered && !!showing?.piece.price?.sizes?.length;
  const sizeNow = slot.current === null ? size : (pieceSizes[slot.current] ?? null);
  const stepNow = slot.current === null ? step : (pieceSteps[slot.current] ?? null);
  const addToo = (item: Suggestion) => {
    track("suggestion.added", { piece: item.piece.id, kind: item.kind });
    // The studio's own look asks nothing; the server is told once per visit.
    if (!admin && !suggestionWanted.current) {
      suggestionWanted.current = true;
      void fetch(`${API_BASE}/api/order-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, piece: item.piece.id, from: "suggestion" }),
      }).catch(() => {
        suggestionWanted.current = false;
      });
    }
    setTogetherAdd((ask) => ({
      n: (ask?.n ?? 0) + 1,
      pieces: [{ id: showing!.piece.id, size: sizeNow }, { id: item.piece.id, size: null }],
    }));
  };
  /* "See this one", and the chip back. */
  const seeOne = (item: Suggestion) => {
    track("suggestion.opened", { piece: item.piece.id, kind: item.kind, role: item.role });
    showPiece(item.piece.id === pieceList[primaryAt]?.id ? null : item.piece.id);
  };

  return (
    <PageContext.Provider
      value={{
        token,
        admin,
        language: preview.language,
        currency: preview.currency ?? "EUR",
        promoCode: preview.promoCode,
        name: preview.firstName ?? "",
        ...(paidArrives ? { paidArrives } : {}),
        checkout: canPay,
        ordered,
        onPaid: markPaid,
        refresh,
        refreshOthers: loadOthers,
        size: sizeNow,
        onSize,
        step: stepNow,
        onStep,
        qtys,
        onQty,
        baseLimits,
        bases,
        onBase,
        cutouts,
        picks,
        onPick,
        visits,
      }}
    >
      <BasketContext.Provider value={basketInfo}>
      <Shell picker={
          <LanguagePicker language={preview.language} words={t} onChange={chooseLanguage} />
        }>
        {/* "We're taking care of the rest" is no longer true once it is there:
            the steps say it has arrived, and that is enough. */}
        {paidLine && !(paidLine.key === "orderedNote" && preview.fulfilment?.step === "arrived") && (
          <p className={paidLine.tone} style={{ margin: "0 0 14px" }}>
            {t.t(paidLine.key)}
            {/* Paid on this visit in the weeks Christmas is no longer
                promised, while the printable card is being sent (the
                server's PRINTABLE_CARD): that it is coming, by email. Only
                beside a thank-you for this visit's payment: a page paid for
                earlier says nothing about it. */}
            {paidLine.key === "paidThanks" && preview.delivery?.late && preview.delivery.card && (
              <>
                <br />
                {t.t("paidCard")}
              </>
            )}
          </p>
        )}
        {preview.ordered && preview.fulfilment && (
          <OrderProgress fulfilment={preview.fulfilment} language={preview.language} words={t} />
        )}

        <h1 className={arrived ? "hello arrive" : "hello"}>
          {name ? t.t("hereNamed", { name }) : t.t("here")}
        </h1>

        {/* Every preview they asked for, when there is more than this one:
            straight under the heading, so somebody who asked for two sees at
            once that both are here. */}
        {others && others.length > 1 ? (
          <Others previews={others} words={t} />
        ) : othersWait && !others ? (
          <div className="others-wait" aria-hidden="true" />
        ) : null}

        {/* Another piece than the one the page is about is on show: the way
            back to it. */}
        {showing && showingAt !== primaryAt && (
          <BackChip
            name={t.t(KIND_KEY[pieceList[primaryAt]?.kind ?? preview.kind] ?? "kindMinime")}
            onBack={() => showPiece(null)}
            words={t}
          />
        )}

        {/* Says what the cards are before the first of them: any of the pieces,
            one or all, and what more than one is worth. */}
        {cards && <PiecesIntro pieces={pieceList} fallbackKind={preview.kind} words={t} />}

        {/* The rail and the cards in one plain block, so the rail is held at
            the top while the cards go by and leaves with the last of them,
            rather than over the summary, the proof and the footer. */}
        <div>
        {/* Held at the top while the cards go by: which they are, which are in
            the order, and a tap on one goes to its card. */}
        {canPick && <PieceRail pieces={togetherList} words={t} />}

        {/* The page shows every piece as a card, or the one piece it is
            about, or the one it was asked to show: a page of several in the
            row layout goes through exactly the one-piece path below, for that
            one piece. */}
        {(showing ? [showing] : drawings).map(({ piece, mockup, model, video }, at) => {
          // The keys the base is kept under, read from the first: a piece of
          // several by its own, the page's piece by "page", and the piece a
          // rowed page shows by the key its sheet and "Order them together"
          // keep it under (qtyKey in Quantity.tsx), so it follows the piece.
          const baseKeys = cards
            ? [`piece:${piece.id}`, qtySel(pieceKey("", piece.id))]
            : rowed
              ? [qtySel(pieceKey("", piece.id))]
              : ["page", `piece:${piece.id}`, qtySel(pieceKey("", piece.id))];
          // A statue drawn on a transparent background is showing only standing
          // on its base, in the hero; the base's chips and message under it.
          const onBase = !!mockup?.cutout && takesBase(baseKindOf(piece.kind ?? preview.kind, piece.price));
          return (
          <Hero
            id={cards ? `piece-${piece.id}` : undefined}
            className={arrived ? "arrive" : undefined}
            key={piece.id || "one"}
            fixing={
              fixing && fixing.subjectIds.includes(sid(piece.id)) ? (
                <div className="stack">
                  <Drawing
                    words={t}
                    mode="fix"
                    photoUrl={mockup?.url ?? null}
                    since={fixing.since}
                    phase={fixing.phase}
                    sketch={fixing.sketches[sid(piece.id)] ?? null}
                    done={!!fixDone}
                  />
                  <p className="small muted" style={{ margin: 0 }}>
                    {t.t("fixWhat")}
                  </p>
                </div>
              ) : null
            }
            /* The hat and the sweater, under the drawing they change, in
               the season and where the server says this piece can be
               redrawn while they watch. A redraw greys this piece as a
               change being fixed does; a drawing put back by Undo is simply
               read again. Hidden, not unmounted, while anything is being
               drawn, so its Undo is still there when the new one lands. */
            look={
              <ChristmasLook
                piece={piece}
                busy={!!fixing}
                words={t}
                onRedraw={() => startFixing(true, [sid(piece.id)])}
                onReread={() => load("poll")}
              />
            }
            stage={
              onBase && mockup
                ? (events) => (
                    <BaseStage
                      kind={piece.kind ?? preview.kind}
                      offer={piece.price}
                      drawingUrl={mockup.url}
                      keys={baseKeys}
                      words={t}
                      {...events}
                    />
                  )
                : undefined
            }
            base={
              <BaseShowcase
                kind={piece.kind ?? preview.kind}
                offer={piece.price}
                drawingUrl={mockup?.url ?? null}
                keys={baseKeys}
                scene={!onBase}
                words={t}
              />
            }
            mockup={mockup}
            model={model}
            video={video}
            photoUrl={piece.photoUrl ?? null}
            /* Only when there is more than one: the product and where it sits
               ("Keychain · 2 of 3"), above their own photo underneath the
               drawing, so two of the same product at the same price are still
               told apart at a glance. The one piece a rowed page shows is
               named by its product alone: it is one of several, and the row
               under its price offers the others by name. */
            heading={
              cards
                ? t.t("pieceNamed", {
                    name: t.t(KIND_KEY[piece.kind ?? preview.kind] ?? "kindMinime"),
                    n: String(at + 1),
                    total: String(drawings.length),
                  })
                : rowed
                  ? t.t(KIND_KEY[piece.kind ?? preview.kind] ?? "kindMinime")
                  : null
            }
            /* Its own question, asked of this piece alone: the answer, any
               change and the redraw are all about it, and the others go on
               as they were. Keyed as the page-level card is, and for the
               same reason (see below). Not while any change is being fixed,
               since the one drawing that is grey is not the only thing that
               would be answered. */
            feedback={
              cards && !fixing ? (
                <Feedback
                  key={`${piece.id}-${saidOf(piece.id) === "issue" ? "issue" : "open"}`}
                  token={token}
                  words={t}
                  said={saidOf(piece.id)}
                  pieceId={sid(piece.id)}
                  onChangeSent={(live) => startFixing(live, [sid(piece.id)])}
                />
              ) : null
            }
            /* Pieces are independent: they can have any number of them, one or
               all. Each is offered on its own with its own price and its own
               listing, and "Order them together" under the cards is for
               wanting more than one. One piece keeps the single price card and
               the sticky bar it has always had. */
            footer={
              cards ? (
                <PieceOffer
                  piece={piece}
                  fallbackKind={preview.kind}
                  drawingUrl={mockup?.url ?? null}
                  words={t}
                  autoOpen={payOpen && !!payPiece && piece.id === payPiece}
                  othersOffered={pieceList.some((other) => !!other.price && !!other.etsyUrl)}
                />
              ) : null
            }
            /* One pair of someone else's preview and the printed piece, of
               this product, under the drawing: the strip of them follows
               the price, and a third of the people who open the page never
               get that far. Not on a page of cards, where it would be said
               once per card. */
            prints={cards ? null : (pairsFor(showingKind)[0] ?? null)}
            words={t}
          />
          );
        })}
        </div>

        {/* The price on the first screen, for the half of the pages the
            layout test gives it to (PageLayout.priceLine): straight under the
            drawing, before the question about it. Not on cards, which carry
            their prices, and not once it is paid for. */}
        {layout.priceLine && !cards && !ordered && (
          <PriceLine price={view.price} delivery={preview.delivery} language={preview.language} words={t} />
        )}

        {/* Several pieces in one payment, under the cards that offer each on
            its own: three dogs are three keychains, and one payment is all a
            page takes. With the pieces of their other previews, where those
            can be paid for here too, on a page of one piece as well. Only
            where the page takes the payment itself; it says nothing when
            there is only one piece to pay for. On a page that shows one piece
            at a time it has no card of its own: it opens from the "Add ...
            too" of a suggestion, with the showing piece and that one in it. */}
        <OrderAll
          pieces={togetherPieces(drawings)}
          others={others}
          fallbackKind={preview.kind}
          words={t}
          autoOpen={payOpen && payPiece === "all"}
          ask={togetherAsk}
          basket={canPick}
          quiet={rowed}
          add={togetherAdd}
          delivery={preview.delivery}
        />

        {/* What they have chosen and what it comes to, under the cards that
            choose it. */}
        <BasketSummary words={t} delivery={preview.delivery} />

        {/* Before the price on purpose: the first thing asked of them costs one
            tap and is not money, and the sticky bar means nothing is lost by it.
            There is no order button after it: the bar below is the page's one,
            and two identical buttons in one view read as a mistake. On a page of
            cards this question is under each piece instead (the Hero's
            `feedback`), because an answer about "it" has to say which; a page
            that shows one piece at a time asks it of that piece, with its id. */}
        {/* Keyed by whether a change is open, because it only reads what it
            was told when it mounts: a change redone while the tab sat open came
            back on a re-read with the new figurine above a card still saying it
            was being redone, and no "Love it" under it until a reload. Only
            that. It used to be keyed by the answer itself, so a "Love it" tapped
            on this visit came back on the next re-read (any return to the tab)
            as a different key, and the card was built again. The card already
            knows it was loved, having been told so by the tap. A rowed page
            keys it by its piece too, so the next piece starts from its own
            answer and not from the last one's. */}
        {/* Not while a change is being fixed in front of them: the answer to
            it is the drawing above, and the question comes back with it. */}
        {!cards && !fixing && (
          <Feedback
            key={`${showing?.piece.id ?? "fb"}-${(showing ? saidOf(showing.piece.id) : said) === "issue" ? "issue" : "open"}`}
            token={token}
            words={t}
            said={showing ? (saidOf(showing.piece.id) ?? (lovedHere.includes(showing.piece.id) ? "loved" : null)) : said}
            pieceId={showing ? sid(showing.piece.id) : undefined}
            onAnswered={
              showing
                ? (reaction) => {
                    const id = showing.piece.id;
                    if (reaction === "loved") setLovedHere((before) => (before.includes(id) ? before : [...before, id]));
                  }
                : undefined
            }
            onChangeSent={(live) =>
              startFixing(live, showing ? [sid(showing.piece.id)] : pieceList.map((piece) => sid(piece.id)))
            }
          />
        )}

        {/* The price straight after the question about the drawing. It used
            to follow the proof strip, and a third of the people who opened
            the page never reached it: the number is the decision, and the
            sizes under it are the first step of making it. */}
        <PriceCard
          preview={view}
          words={t}
          datedMultiOnly={rowed}
          onPickSize={(picked) => {
            onSize(picked);
            setSizeAsk((ask) => ({ size: picked, n: (ask?.n ?? 0) + 1 }));
          }}
        />

        {/* The other pieces of the page, one compact row each, right after the
            price they would be weighed against: another product of the same
            people is a swap ("See this one"), another subject joins the same
            parcel ("Add this one too"). Only on a page that shows one piece at
            a time. Not once it is paid for: the pieces it offers could not be
            ordered from here any more, and a tap on one would only be browsing
            that the numbers page then counts as interest. */}
        {showing && !ordered && (
          <Suggestions
            items={suggestions}
            together={together}
            language={preview.language}
            words={t}
            onSee={seeOne}
            onAdd={addToo}
          />
        )}

        {/* After the price: what they are about to pay for is a thing in the
            hand, and everything above this is a picture on a screen. */}
        <Proof kind={showingKind} words={t} />

        <DetailsCard preview={view} words={t} />

        {/* What other buyers said, in their words: the trust pill above says
            "5.0 on Etsy" and nothing else. */}
        <Reviews language={preview.language} words={t} />

        {/* After the trust content, not between the price and it: the 10% off
            asks for an email address, which is a second ask beside the one
            they are deciding on. Never inside the order sheet, where people
            already stop. The server decides whether it is offered at all;
            nothing once it is paid for. */}
        {!ordered && <NewsletterOffer preview={preview} words={t} />}

        {/* Only a date still ahead, and not for someone who has ordered or
            whose payment is on its way: their files are kept for it, and a
            day already gone by is not a warning. */}
        {preview.purgeAfter && !ordered && Date.parse(preview.purgeAfter) > Date.now() && (
          <p className="tiny muted center" style={{ margin: "2px 0 0" }}>
            {t.t("deleted", { date: formatDate(preview.purgeAfter, preview.language) })}
          </p>
        )}

        <Footer words={t} language={preview.language} contact />

        {/* Last child of the wrap: its negative bottom margin is tuned to cancel
            the wrap's padding, and it is sticky rather than fixed so the keyboard
            pushes it instead of covering it.
            Always on a page of one piece, and on a page of several that shows one
            at a time, where it orders exactly that piece (its id, product, price
            and listing) and is made again when the piece changes, so nothing
            chosen for one is carried to the next. Not on cards: three
            alternatives cannot share one button, and a bar that picked one of
            them would send the two people who wanted the others to the wrong
            listing.
            Paid for, the button inside it steps aside and the bar goes with it
            (it is empty, and the stylesheet hides an empty one). It is still
            drawn, because the sheet that just took the payment belongs to the
            button, and taking the bar away would close it mid-thank-you. */}
        {canPick && (
          <div className="actionbar">
            <BasketBar
              words={t}
              onContinue={basketInfo.open}
              onChoose={() =>
                document.getElementById(`piece-${pieceList[0]?.id ?? ""}`)?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
            />
          </div>
        )}
        {!cards && (
          <div className="actionbar">
            {/* Not for "piece=all", which is "Order them together"'s own, on a
                page of one piece with their other previews in it. On a page of
                several, only the piece the mail named, which is the one showing. */}
            <OrderBar
              key={showing?.piece.id ?? "page"}
              preview={view}
              words={t}
              from="bar"
              autoOpen={payOpen && payPiece !== "all" && (!showing || payPiece === showing.piece.id)}
              sizeAsk={sizeAsk}
              {...(showing
                ? {
                    offer: showing.piece.price ?? null,
                    etsyUrl: showing.piece.etsyUrl ?? "",
                    pieceKind: showingKind,
                    pieceId: showing.piece.id,
                    drawingUrl: showing.mockup?.url ?? showing.piece.photoUrl ?? null,
                  }
                : {})}
            />
          </div>
        )}
      </Shell>
      </BasketContext.Provider>
    </PageContext.Provider>
  );
}

/** What became of the checkout they have just come back from: still being
 *  asked, paid, finished but not yet paid by the bank, not paid at all, or
 *  not known because the server could not be asked. */
type Returned = "checking" | "paid" | "processing" | "failed" | "unknown";

/** The line at the top of the page about paying, if there is one to say.
 *
 *  What happened on this visit first, then what the server says, then what
 *  could not be found out, and only then a return that did not pay: a payment
 *  the server has seen, or one on its way from the bank, is the truer news
 *  even when the session they came back from was a cancelled one. */
function lineFor({
  visit,
  returned,
  ordered,
  processing,
}: {
  visit: "checking" | "paid" | "processing" | null;
  returned: Returned | null;
  ordered: boolean;
  processing: boolean;
}): { key: string; tone: string } | null {
  if (visit === "paid" || (visit === "processing" && ordered)) {
    return { key: "paidThanks", tone: "notice notice-good" };
  }
  if (visit === "processing") return { key: "paidProcessing", tone: "notice" };
  if (ordered) return { key: "orderedNote", tone: "notice notice-good" };
  if (processing) return { key: "paidProcessing", tone: "notice" };
  // Paid in the sheet a moment ago and still being asked about: the sheet is
  // saying so, and an older return has nothing to add.
  if (visit === "checking") return null;
  if (returned === "unknown") return { key: "payUnconfirmed", tone: "notice" };
  if (returned === "failed") return { key: "payNotCompleted", tone: "notice notice-bad" };
  return null;
}

/** The session they paid in, into the address, as a bank's return puts it
 *  there. The rest of the address stays, except the order mail's ?pay=1, which
 *  asked for the sheet to open and has been answered by the payment: a reload
 *  must ask how that went, not open a new checkout. Its &piece= stays: without
 *  ?pay it opens nothing, and on a page of several that shows one piece at a
 *  time it is the piece that was paid for, which the page cannot otherwise
 *  tell and which a reload should show again. */
function keepPaid(session: string) {
  if (!session.startsWith("cs_")) return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("paid", session);
    url.searchParams.delete("pay");
    window.history.replaceState(window.history.state, "", url);
  } catch {
    /* This visit knows it anyway; only a reload would not. */
  }
}

/** The session they came back from, out of the address, once there is nothing
 *  more to ask about it. Only if it is still that one: a payment made in the
 *  sheet since has put its own there. */
function dropPaid(session: string) {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("paid") !== session) return;
    url.searchParams.delete("paid");
    window.history.replaceState(window.history.state, "", url);
  } catch {
    /* The address keeps it, and the next read asks again. */
  }
}

/** What can stand in for a preview: a link that opens nothing (a 404, and only
 *  a 404), no connection at all, or a read that failed some other way and is
 *  worth trying again. */
type Trouble = "missing" | "offline" | "error";
const TROUBLE_COPY: Record<Trouble, string> = {
  missing: "missing",
  offline: "offline",
  error: "loadFailed",
};

/** A read the server answered with something other than the preview. */
class ReadFailed extends Error {
  status: number;
  constructor(status: number) {
    super(String(status));
    this.status = status;
  }
}

/** How long after coming back to the tab it is read again. Long enough to be
 *  past the moment iOS has not yet given the page its network back, short
 *  enough that a change redone while they were away is there when they look. */
const RETURN_DELAY_MS = 800;

/** How long after a payment could not be confirmed it is asked about once
 *  more, for someone who stays on the page rather than leaving and coming
 *  back: long enough for a connection that dropped, or a server that was
 *  having a bad moment, to have come back. */
const UNCONFIRMED_AGAIN_MS = 20_000;

/** How often an instant preview being drawn is checked. A drawing takes a
 *  minute or two, so this is at most a few seconds after it lands. */
const POLL_MS = 5000;
const SKETCH_POLL_MS = 2500;
const PHOTO_CHECK_POLL_MS = 7000;

/** Up to how much later than its moment a page open across midnight, or
 *  across the end of a window, reads itself again (rereadDelay), so the open
 *  pages are not all asked for in the same second. */
const REREAD_SPREAD_MS = 20_000;

/** The drawing on the page for each of these pieces, in order, "" for none. */
function mockupsOf(data: PreviewDoc, subjectIds: string[]): string[] {
  return subjectIds.map(
    (id) =>
      (data.artifacts ?? []).find((a) => a.kind === "mockup" && sid(a.subjectId) === id)?.url ?? ""
  );
}

/** How long "Done!" stays before the figurine replaces it. */
const FINISH_MS = 1200;

/** How long after the page is ready Stripe.js is fetched for the order sheet:
 *  after the figurine's own image, before anyone has read far enough to press
 *  "I want to order". */
const WARM_PAY_MS = 1500;

/** The same test index.html makes before it passes ?src= on. */
function mailSource(value: string | null): string | null {
  return value && /^[a-z0-9_]{1,24}$/.test(value) ? value : null;
}

/** A read of the studio's look (?admin=1), with the owner's sign-in where
 *  this browser has one, as the checkout's is (PayForm.tsx). The server shows
 *  the owner what is switched on for the owner alone (the multi-buy in its
 *  "admin" mode) only to a request that proves who is asking: ?admin=1 is
 *  only an address, which anyone can type. Signed out, it reads as it always
 *  did, and the sign-in is only ever imported here, never on a customer's
 *  page. The early read index.html made, without the sign-in, is set aside. */
async function adminRead(url: string): Promise<Response> {
  takeEarlyRead(url);
  const headers: Record<string, string> = {};
  try {
    const { auth } = await import("../admin");
    await auth.authStateReady();
    const idToken = await auth.currentUser?.getIdToken();
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
  } catch {
    /* Read as anyone would: the page is the same, without what is the
       owner's alone. */
  }
  return fetch(url, { headers });
}

/** The first read, if index.html started it before this bundle arrived, and
 *  only if it asked for exactly what this page is about to ask for. Taken once:
 *  every later read is a new request. */
function takeEarlyRead(url: string): Promise<Response> | null {
  const holder = window as Window & { __previewRead?: { url: string; response: Promise<Response> } };
  const early = holder.__previewRead;
  if (!early) return null;
  delete holder.__previewRead;
  return early.url === url ? early.response : null;
}
